/* eslint-disable no-restricted-syntax -- 도메인 산재 색상 (1~2건/색) — 디자인 시스템 토큰화 효율 < 50%, 별도 디자인 정리 plan으로 분리 */
import { recipe } from '@vanilla-extract/recipes';
import { keyframes, style } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { mobile, reducedMotion } from '../../theme/media';
import { spin } from '../../keyframes/shared.css';

const microGlitch = keyframes({
  '0%': { transform: 'translateX(0)' },
  '25%': { transform: 'translateX(2px)' },
  '50%': { transform: 'translateX(-1px)' },
  '75%': { transform: 'translateX(1px)' },
  '100%': { transform: 'translateX(0)' },
});

export const buttonBase = style({});

export const buttonRecipe = recipe({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: vars.spacing.gap.sm,
    borderRadius: vars.spacing.borderRadius.md,
    fontFamily: vars.typography.fontFamily.primary,
    fontWeight: vars.typography.fontWeight.bold,
    letterSpacing: vars.typography.letterSpacing.wide,
    textTransform: 'uppercase' as const,
    cursor: 'pointer',
    transition: `background ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, border-color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, box-shadow ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, text-shadow ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
    border: 'none',
    outline: 'none',
    position: 'relative',
    overflow: 'hidden',
    '@media': {
      [reducedMotion]: {
        transition: 'none',
        animation: 'none',
      },
    },
    selectors: {
      '&:focus-visible': {
        outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
        outlineOffset: vars.focus.ring.offset,
      },
      '&:active:not(:disabled)': {
        animation: `${microGlitch} ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
      },
    },
  },

  variants: {
    variant: {
      primary: {
        color: vars.button.primary.color,
        border: `1px solid rgba(100, 200, 255, 0.25)`,
        background: `radial-gradient(ellipse at 50% 30%, ${vars.opacityScale.whiteOverlay.soft} 0%, transparent 60%), ${vars.button.primary.background}`,
        letterSpacing: vars.typography.letterSpacing.label,
        textShadow: vars.typography.textShadow.onGlass,
        boxShadow: `inset 0 1px 0 ${vars.opacityScale.whiteOverlay.bold}, inset 0 -1px 0 ${vars.opacityScale.blackOverlay.strong}, inset 0 0 12px ${vars.opacityScale.cyan.faint}, ${vars.bright.glow.primary}`,
        selectors: {
          '&:hover:not(:disabled)': {
            background: `radial-gradient(ellipse at 50% 30%, ${vars.opacityScale.whiteOverlay.soft} 0%, transparent 55%), ${vars.button.primary.hoverGradient}`,
            borderColor: 'rgba(100, 200, 255, 0.4)',
            boxShadow: `inset 0 1px 0 ${vars.opacityScale.whiteOverlay.intense}, inset 0 -1px 0 ${vars.opacityScale.blackOverlay.firm}, inset 0 0 20px ${vars.opacityScale.cyan.notable}, ${vars.bright.glow.primary}, 0 4px 16px ${vars.opacityScale.cyan.strong}`,
            textShadow: vars.typography.textShadow.onGlassStrong,
          },
        },
      },
      secondary: {
        border: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
        background: `radial-gradient(ellipse at 50% 30%, ${vars.opacityScale.whiteOverlay.soft} 0%, transparent 60%), ${vars.button.secondary.background}`,
        color: vars.bright.primary.light,
        backdropFilter: vars.surface.liquidGlass.backdropFilter.light,
        boxShadow: `inset 0 1px 0 ${vars.opacityScale.whiteOverlay.airy}, inset 0 -1px 0 ${vars.opacityScale.blackOverlay.mediumStrong}`,
        selectors: {
          '&:hover:not(:disabled)': {
            background: `radial-gradient(ellipse at 50% 30%, ${vars.opacityScale.whiteOverlay.airy} 0%, transparent 55%), ${vars.button.secondary.hoverBg}`,
            borderColor: vars.bright.primary.default,
            color: vars.bright.primary.light,
            boxShadow: `inset 0 1px 0 ${vars.opacityScale.whiteOverlay.notable}, inset 0 -1px 0 ${vars.opacityScale.blackOverlay.strong}, inset 0 0 16px ${vars.opacityScale.primaryBlue.light}, 0 0 10px ${vars.surface.highlight.medium}`,
          },
        },
      },
      // ghost = 투명 + 무테. 업계 관례(Chakra `ghost` · Material `text`)의 조용한 보조 액션이다 —
      // 2026-09-01 점선을 걷었다(이름만 보고 집으면 예상과 다른 모양이 나오던 유일한 칸). 호출부 82곳 무변경.
      // 테두리를 none 이 아니라 투명 1px 실선으로 두는 이유: hover 가 borderColor 만 바꿔 현행과 같은
      // 실선 테두리를 밀림 없이 띄운다(none 이면 폭 medium 3px 가 살아나 1px 씩 밀린다).
      ghost: {
        background: 'transparent',
        border: '1px solid transparent',
        color: vars.typography.textColor.secondary,
        boxShadow: 'none',
        textShadow: 'none',
        textTransform: 'none' as const,
        letterSpacing: 'normal',
        selectors: {
          '&:hover:not(:disabled)': {
            background: vars.button.ghost.hoverBg,
            borderColor: vars.bright.primary.default,
            color: vars.bright.primary.light,
          },
        },
      },
      danger: {
        background: `radial-gradient(ellipse at 50% 30%, ${vars.opacityScale.whiteOverlay.airy} 0%, transparent 60%), ${vars.button.danger.gradient}`,
        border: `1px solid ${vars.opacityScale.errorRed.firm}`,
        letterSpacing: vars.typography.letterSpacing.label,
        color: vars.typography.textColor.primary,
        boxShadow: `inset 0 1px 0 ${vars.opacityScale.whiteOverlay.notable}, inset 0 -1px 0 ${vars.opacityScale.blackOverlay.firm}, inset 0 0 12px ${vars.opacityScale.errorRed.subtle}, ${vars.bright.glow.red}`,
        selectors: {
          '&:hover:not(:disabled)': {
            background: `radial-gradient(ellipse at 50% 30%, ${vars.opacityScale.whiteOverlay.prominent} 0%, transparent 55%), ${vars.button.danger.hoverGradient}`,
            borderColor: `${vars.opacityScale.errorRed.firm}`,
            boxShadow: `inset 0 1px 0 ${vars.opacityScale.whiteOverlay.strong}, inset 0 -1px 0 ${vars.opacityScale.blackOverlay.prominent}, inset 0 0 20px ${vars.opacityScale.errorRed.light}, ${vars.bright.glow.red}, ${vars.button.danger.hoverGlowExtra}`,
          },
        },
      },
      success: {
        background: `radial-gradient(ellipse at 50% 30%, ${vars.opacityScale.whiteOverlay.airy} 0%, transparent 60%), ${vars.button.success.gradient}`,
        border: `1px solid ${vars.button.success.border}`,
        color: vars.typography.textColor.primary,
        boxShadow: `inset 0 1px 0 ${vars.opacityScale.whiteOverlay.notable}, inset 0 -1px 0 ${vars.opacityScale.blackOverlay.strong}, ${vars.button.success.glow}`,
        selectors: {
          '&:hover:not(:disabled)': {
            background: `radial-gradient(ellipse at 50% 30%, ${vars.opacityScale.whiteOverlay.prominent} 0%, transparent 55%), ${vars.button.success.hoverGradient}`,
            boxShadow: `inset 0 1px 0 ${vars.opacityScale.whiteOverlay.strong}, inset 0 -1px 0 ${vars.opacityScale.blackOverlay.firm}, ${vars.button.success.hoverGlow}`,
          },
        },
      },

      // ── 모티프-중립 표면 (TIER 2·3 전용) ──
      // DESIGN.md §Atom Standards 1-1: variant 선택은 페이지 모티프 × 요소 Tier 의 함수다.
      // 목록 행·인라인 액션·보조 컨트롤은 §Visual Hierarchy Rules 가 glow 를 막으므로
      // 페이지 모티프와 무관하게 이 표면을 쓴다. 실측(2026-08-29) 로컬 버튼 460건 중
      // text 149건(32%) · outline 107건(23%) 이 대응 variant 가 없어 page-local 로 나가 있었다.

      // link = 인라인 텍스트 액션(되돌리기·더보기·상세). 면을 만들지 않고 글자만 남긴다 —
      // 파란 글자 + hover 밑줄이라 업계 표준 이름은 `link` 다(2026-09-01 `text` 에서 개명, 프로덕션 호출부 0).
      link: {
        background: 'transparent',
        border: 'none',
        color: vars.bright.primary.default,
        boxShadow: 'none',
        textShadow: 'none',
        textTransform: 'none' as const,
        letterSpacing: 'normal',
        selectors: {
          '&:hover:not(:disabled)': {
            color: vars.bright.primary.light,
            textDecoration: 'underline',
          },
          '&:active:not(:disabled)': {
            animation: 'none',
          },
        },
      },
      // outline = 투명 + 실선 hairline. ghost 와 같은 표면이되 점선이 아니다 —
      // 점선을 실제로 원한 것은 9건뿐이고 실선 수요가 107건이다.
      outline: {
        background: 'transparent',
        border: `1px solid ${vars.surface.border.secondary}`,
        color: vars.typography.textColor.secondary,
        boxShadow: 'none',
        textShadow: 'none',
        textTransform: 'none' as const,
        letterSpacing: 'normal',
        selectors: {
          '&:hover:not(:disabled)': {
            borderColor: vars.bright.primary.default,
            color: vars.typography.textColor.primary,
            background: vars.surface.highlight.faint,
          },
          '&:active:not(:disabled)': {
            animation: 'none',
          },
        },
      },

      // soft = 채움 + hairline. `outline`(투명+실선)과 `flat`(원색 채움) 사이의 조용한 면이다.
      // `DESIGN.md` §1-1 이 중립 표면 넷에 이름만 올려 두고 정의가 없던 칸이라 실측으로 정했다:
      // `cursor: 'pointer'` 스타일 566건 중 **채움+실선 86건**, 그중 버튼성 이름 52건이고
      // 저알파 중립 채움이 27건이다(`exportButton`·`sendButton`·`detailButton`·`editButton`·
      // `newButton`·`collapseBtn`…). 반대로 채움+무테의 중립 틴트 30건은 열어 보면 대부분
      // 버튼이 아니라 드롭다운 `option`·목록 `item`·`gridCell`·`drawerBackdrop` 이라 이 칸이 아니다.
      soft: {
        background: vars.surface.highlight.faint,
        border: `1px solid ${vars.surface.border.secondary}`,
        color: vars.typography.textColor.secondary,
        boxShadow: 'none',
        textShadow: 'none',
        textTransform: 'none' as const,
        letterSpacing: 'normal',
        selectors: {
          '&:hover:not(:disabled)': {
            background: vars.surface.highlight.medium,
            borderColor: vars.bright.primary.default,
            color: vars.typography.textColor.primary,
          },
          '&:active:not(:disabled)': {
            animation: 'none',
          },
        },
      },

      // ── 운영자 콘솔(HR·Finance·CEO·SysAdmin) 절제 모티프 ──
      // 글래스·gradient·glow·글리치를 전부 끈다. 계약: docs/plan/operator-console-restraint-atoms-plan.md
      // 절제는 *표면* 축이다 — radius 는 건드리지 않고 base 의 md 를 상속한다. radius 를 위계 축과
      // 모티프 축이 겸하고 있어 게임 UI 면이 이 variant 를 못 쓰고 page-local 로 우회하고 있었다.
      // chip = 필터·모드 토글. 눌림은 호출부가 넘기는 aria-pressed 를 그대로 시각에 쓴다(별도 prop 없음).
      chip: {
        background: 'transparent',
        border: `1px solid ${vars.surface.border.secondary}`,
        color: vars.typography.textColor.tertiary,
        fontWeight: vars.typography.fontWeight.semibold,
        letterSpacing: vars.typography.letterSpacing.label,
        textTransform: 'none' as const,
        boxShadow: 'none',
        textShadow: 'none',
        overflow: 'visible',
        selectors: {
          '&:hover:not(:disabled)': {
            borderColor: vars.bright.primary.default,
            color: vars.typography.textColor.primary,
          },
          '&[aria-pressed="true"]': {
            color: vars.bright.primary.light,
            background: vars.surface.highlight.medium,
            borderColor: vars.bright.primary.default,
          },
          '&:active:not(:disabled)': {
            animation: 'none',
          },
        },
      },
      // flat = 절제 폼의 제출 버튼. primary.default 위 흰 글자는 AA 미달이라 primary.dark로 채운다.
      // hover는 채움을 밝히지 않고 안쪽 hairline만 켠다. 합성 대비는 brandAccentContract가 검증한다.
      flat: {
        background: vars.bright.primary.dark,
        border: 'none',
        color: vars.typography.textColor.primary,
        fontWeight: vars.typography.fontWeight.semibold,
        letterSpacing: vars.typography.letterSpacing.label,
        boxShadow: 'none',
        textShadow: 'none',
        selectors: {
          '&:hover:not(:disabled)': {
            boxShadow: `inset 0 0 0 1px ${vars.bright.primary.light}`,
          },
          '&:active:not(:disabled)': {
            animation: 'none',
          },
        },
      },
    },

    // ── 톤 축 (의미) — 표면 축과 직교한다 ──
    // 표면이 형태를 정하고 톤은 그 안의 색만 바꾼다. variant 로 풀면 9칸이 21칸이 되고
    // 다음 톤이 붙을 때 또 곱해진다. 칸 자체는 비어 있고 실제 색은 compoundVariants 가 준다 —
    // 같은 danger 라도 text 는 글자색, outline 은 테두리, flat 은 채움으로 나가기 때문이다.
    // 실측(2026-08-31): 상태색을 쓰는 page-local 버튼 선언 43건(club 4건 제외 39건) 중
    // danger 32건의 내역이 text/icon 21 · fill 8 · outline 3 이다. 축을 flat 에만 걸면 8건만 덮는다.
    tone: {
      default: {},
      danger: {},
      warning: {},
    },

    size: {
      // xs = 실수요 밀도. chip 의 sm compound 가 "FinancePage filterBtn 실측(6px 14px · xs)" 으로
      // 담고 있던 값을 size 축으로 올린 것이다 — 우회로였던 compound 는 이 칸으로 흡수됐다.
      // 근거: 로컬 버튼 460건의 padding 분포가 세로 4~7px·가로 8~14px 에 몰려 있고
      // md 의 12px 20px 는 0건이다 (DESIGN.md §Atom Standards 2, 2026-08-29).
      xs: {
        padding: '6px 14px',
        fontSize: vars.typography.fontSize.xs,
        minHeight: 0,
        '@media': {
          [mobile]: {
            minHeight: '44px',
          },
        },
      },
      sm: {
        padding: '8px 16px',
        fontSize: vars.typography.fontSize.sm,
        minHeight: '32px',
        '@media': {
          [mobile]: {
            minHeight: '44px',
          },
        },
      },
      md: {
        padding: '12px 20px',
        fontSize: vars.typography.fontSize.base,
        minHeight: '40px',
      },
      lg: {
        padding: '16px 28px',
        fontSize: vars.typography.fontSize.md,
        minHeight: '48px',
      },
    },

    fullWidth: {
      true: { width: '100%' },
    },

    isDisabled: {
      true: {
        opacity: 0.6,
        color: vars.typography.textColor.disabled,
        cursor: 'not-allowed',
        pointerEvents: 'none' as const,
        boxShadow: 'none',
      },
    },
  },

  // chip × sm compound 는 size 축의 xs 로 흡수됐다(2026-08-29). 그 값이 곧 실수요 밀도라
  // 특정 variant 에 묶어 둘 이유가 없었다 — 호출부는 size="xs" 로 넘긴다.
  //
  // 여기 있는 것은 톤뿐이고, 톤을 받는 표면은 link·outline·flat·soft 넷이다.
  // 글래스 5종과 chip 은 받지 않는다 — 글래스는 danger·success 를 이미 variant 로 갖고 있어
  // 톤까지 열면 같은 뜻이 두 축에 앉고, chip 은 실측 수요가 0 이다(DESIGN.md §Atom Standards 1-1).
  compoundVariants: [
    // link — 면을 만들지 않으므로 글자색만 바뀐다. base 의 hover 가 색을 primary 로 되돌리므로
    // 여기서 다시 눌러 준다(같은 특이도, compound 가 뒤에 나온다).
    {
      variants: { variant: 'link', tone: 'danger' },
      style: {
        color: vars.bright.status.error,
        selectors: {
          '&:hover:not(:disabled)': {
            color: vars.bright.status.error,
            textDecoration: 'underline',
          },
        },
      },
    },
    {
      variants: { variant: 'link', tone: 'warning' },
      style: {
        color: vars.bright.status.warning,
        selectors: {
          '&:hover:not(:disabled)': {
            color: vars.bright.status.warning,
            textDecoration: 'underline',
          },
        },
      },
    },

    // outline — hairline 과 글자가 함께 톤을 든다. 유휴 테두리는 전용 보더 토큰(.19),
    // hover 에서 전경색 전체로 올라가고 틴트(.10)가 깔린다.
    {
      variants: { variant: 'outline', tone: 'danger' },
      style: {
        border: `1px solid ${vars.bright.status.errorBorder}`,
        color: vars.bright.status.error,
        selectors: {
          '&:hover:not(:disabled)': {
            borderColor: vars.bright.status.error,
            color: vars.bright.status.error,
            background: vars.bright.status.errorBg,
          },
        },
      },
    },
    {
      variants: { variant: 'outline', tone: 'warning' },
      style: {
        border: `1px solid ${vars.bright.status.warningBorder}`,
        color: vars.bright.status.warning,
        selectors: {
          '&:hover:not(:disabled)': {
            borderColor: vars.bright.status.warning,
            color: vars.bright.status.warning,
            background: vars.bright.status.warningBg,
          },
        },
      },
    },

    // flat — 채움이라 상태색을 그대로 못 쓴다. 상태색은 전경용으로 잡힌 값이고,
    // 배경으로 뒤집으면 그 위의 글자가 대비를 잃는다. 두 톤이 서로 다른 방향으로 푼다.
    // danger: 색을 내린다(errorDark, textColor.primary 기준 4.74).
    {
      variants: { variant: 'flat', tone: 'danger' },
      style: {
        background: vars.bright.status.errorDark,
        selectors: {
          '&:hover:not(:disabled)': {
            boxShadow: `inset 0 0 0 1px ${vars.bright.status.error}`,
          },
        },
      },
    },
    // warning: 색은 그대로 두고 글자를 뒤집는다(inverse 기준 7.87). amber 를 어둡게 내리면
    // 갈색이 되어 상태색으로 안 읽힌다. 선례는 DESIGN.md §Semantic Colors 의 아웃브레이크 CTA
    // — "amber fill + dark text, 대비 ≥ 4.5:1".
    {
      variants: { variant: 'flat', tone: 'warning' },
      style: {
        background: vars.bright.status.warning,
        color: vars.typography.textColor.inverse,
        selectors: {
          '&:hover:not(:disabled)': {
            boxShadow: `inset 0 0 0 1px ${vars.typography.textColor.inverse}`,
          },
        },
      },
    },

    // soft — 틴트 채움이라 상태색을 전경으로 그대로 쓸 수 있다(flat 과 갈리는 지점).
    // 채움은 틴트(.10), 테두리는 보더 토큰(.19), 글자는 전경색. hover 는 면을 밝히지 않고
    // 테두리만 전경색으로 올린다 — 면을 밝히면 그 위 글자의 대비가 떨어진다.
    // 실측 수요: CheckInCenter 의 clockOut·break 가 정확히 이 모양이다(틴트 채움 + 상태 보더 + 상태 글자).
    {
      variants: { variant: 'soft', tone: 'danger' },
      style: {
        background: vars.bright.status.errorBg,
        border: `1px solid ${vars.bright.status.errorBorder}`,
        color: vars.bright.status.error,
        selectors: {
          '&:hover:not(:disabled)': {
            background: vars.bright.status.errorBg,
            borderColor: vars.bright.status.error,
            color: vars.bright.status.error,
          },
        },
      },
    },
    {
      variants: { variant: 'soft', tone: 'warning' },
      style: {
        background: vars.bright.status.warningBg,
        border: `1px solid ${vars.bright.status.warningBorder}`,
        color: vars.bright.status.warning,
        selectors: {
          '&:hover:not(:disabled)': {
            background: vars.bright.status.warningBg,
            borderColor: vars.bright.status.warning,
            color: vars.bright.status.warning,
          },
        },
      },
    },
  ],

  defaultVariants: {
    variant: 'primary',
    // 호출부 256건 중 70%(181건)가 size="sm" 을 명시해 md 를 벗어나고 있었고
    // md 를 실제로 지정한 것은 9건(3%)뿐이었다 (DESIGN.md §Atom Standards 2, 2026-08-29).
    size: 'sm',
    tone: 'default',
    fullWidth: false,
    isDisabled: false,
  },
});

export const loadingSpinner = style({
  width: '16px',
  height: '16px',
  border: `2px solid ${vars.button.spinner.border}`,
  borderTopColor: vars.typography.textColor.inverse,
  borderRadius: '50%',
  animation: `${spin} ${vars.animation.duration.slower} linear infinite`,
  '@media': {
    [reducedMotion]: {
      animation: 'none',
    },
  },
});
